import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useTabState - Persists the active tab in the URL search params
 * So refreshing the page keeps you on the same tab
 * Usage: const [tab, setTab] = useTabState('defaultTab', 'tab');
 */
export function useTabState(defaultValue, paramName = 'tab') {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get(paramName) || defaultValue;
  const [tab, setTabInternal] = useState(initial);

  const setTab = useCallback((newTab) => {
    setTabInternal(newTab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newTab === defaultValue) {
        next.delete(paramName);
      } else {
        next.set(paramName, newTab);
      }
      return next;
    }, { replace: true });
  }, [defaultValue, paramName, setSearchParams]);

  return [tab, setTab];
}

/**
 * useAutoRefresh - Auto-refreshes data at a given interval
 * Pauses when the tab/window is not visible
 * Usage: useAutoRefresh(fetchData, 15000);
 */
export function useAutoRefresh(fetchFn, intervalMs = 20000) {
  const intervalRef = useRef(null);
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (!document.hidden) {
          fetchRef.current();
        }
      }, intervalMs);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        fetchRef.current();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [intervalMs]);
}
